import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Trophy, Users, GraduationCap, Building2, MessageCircle } from "lucide-react";
import api from "../lib/api";
import ListingCard, { DEFAULT_BANNERS } from "../components/ListingCard";
import FeaturedCard from "../components/FeaturedCard";
import CategoryTiles from "../components/CategoryTiles";
import { useMobile } from "../hooks/useMobile";
import MobileHome from "../components/MobileHome";
import { BannerCarousel, BannerStrip } from "../components/SponsoredBanners";

const SectionHead = ({ title, link, testId }) => (
  <div className="flex items-center justify-between mb-4">
    <h2 className="font-display text-lg md:text-2xl font-extrabold border-l-4 pl-3" style={{ borderColor: "#A3E635" }}>{title}</h2>
    <Link to={link} data-testid={testId} className="text-xs md:text-sm font-bold text-lime-700">View All →</Link>
  </div>
);

export default function Home() {
  const [all, setAll] = useState([]);
  const [areas, setAreas] = useState([]);
  const [cities, setCities] = useState([]);
  const [points, setPoints] = useState(null);
  const [settings, setSettings] = useState(null);
  const [home, setHome] = useState(null);
  const mobile = useMobile();

  useEffect(() => {
    api.get("/institutions").then((r) => setAll(r.data)).catch(() => {});
    api.get("/institutions/cities").then((r) => { setAreas(r.data.areas || []); setCities(r.data.cities || []); }).catch(() => {});
    api.get("/points/public").then((r) => setPoints(r.data)).catch(() => {});
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/homepage/public").then((r) => setHome(r.data)).catch(() => {});
  }, []);

  const city = cities[0] || "your city";
  const name = settings?.business_name || "DreamCots";
  const heroTitle = (home?.hero_title || "Find your dream coaching in {city}.").replace("{city}", city);
  const sections = (home?.sections || []).filter((s) => s.enabled);

  if (mobile) return <MobileHome all={all} home={home} city={city} name={name} points={points} />;

  return (
    <div>
      {/* Mobile: banner carousel first */}
      <div className="md:hidden pt-4"><BannerCarousel compact /></div>

      {/* Desktop hero */}
      <section className="hidden md:grid max-w-7xl mx-auto px-4 sm:px-8 py-14 md:py-20 grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <span data-testid="hero-live-badge" className="nb-badge" style={{ background: "#FCD34D", padding: "6px 16px", fontSize: 12 }}>Now live in {city}</span>
          <h1 data-testid="hero-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mt-6">{heroTitle}</h1>
          <p className="mt-6 text-base text-slate-600 leading-relaxed max-w-xl">{home?.hero_subtitle}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/institutes" data-testid="hero-find-btn" className="nb-btn nb-btn-lime">Find Institutes <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/register" data-testid="hero-list-btn" className="nb-btn">List your institute</Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-6 text-sm font-semibold">
            <span className="flex items-center gap-2"><Star className="w-4 h-4 fill-amber-400 text-amber-400" /> Verified reviews</span>
            <span className="flex items-center gap-2"><Trophy className="w-4 h-4 text-orange-500" /> Points-based rankings</span>
            <span className="flex items-center gap-2"><Users className="w-4 h-4 text-lime-600" /> Direct enquiry</span>
          </div>
        </div>
        <div className="hidden lg:block lg:col-span-5 relative h-[420px]">
          <img src={DEFAULT_BANNERS.coaching} alt="students studying" className="absolute left-0 top-6 w-60 h-80 object-cover nb -rotate-3" style={{ borderRadius: 18 }} />
          <img src={DEFAULT_BANNERS.school} alt="classroom" className="absolute left-52 top-0 w-56 h-64 object-cover nb rotate-2" style={{ borderRadius: 18 }} />
          <div className="nb absolute left-56 top-64 p-5 w-52 bg-white">
            <p className="nb-label">{city}</p>
            <p className="font-display text-2xl font-extrabold mt-1">{Math.max(areas.length, 1)}+ areas</p>
            <p className="text-xs text-slate-500 mt-0.5">Covered across the city</p>
          </div>
        </div>
      </section>

      <div className="hidden md:block"><BannerCarousel /></div>

      <CategoryTiles categories={home?.categories} />

      {sections.map((s) => {
        const items = all.filter((i) => i.type === s.type).sort((a, b) => (b.badge ? 1 : 0) - (a.badge ? 1 : 0) || b.total_score - a.total_score).slice(0, s.limit || 6);
        if (!items.length) return null;
        return (
          <section key={s.id} data-testid={`home-section-${s.id}`} className="max-w-7xl mx-auto px-4 sm:px-8 pb-10">
            <SectionHead title={s.title} link={`/institutes?type=${s.type}`} testId={`section-view-all-${s.id}`} />
            <div className="flex md:grid md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5 overflow-x-auto snap-x pb-2 -mx-4 px-4 md:mx-0 md:px-0" style={{ scrollbarWidth: "none" }}>
              {items.map((inst) => <FeaturedCard key={inst.id} inst={inst} />)}
            </div>
          </section>
        );
      })}

      {home?.show_how_it_ranks !== false && (
        <section className="border-y-2 border-[#171B33]" style={{ background: "#F2FBE3" }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 md:py-16">
            <h2 className="font-display text-2xl sm:text-4xl font-extrabold tracking-tight max-w-md">How {name} ranks institutes</h2>
            <p className="mt-3 text-slate-600 max-w-xl text-sm md:text-base">A fair points system — the more reviews, admissions and referrals an institute gets, the higher it ranks.</p>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
              {[
                { icon: Star, bg: "#FCD34D", label: `${points?.review_points ?? 10} pts / Review` },
                { icon: Users, bg: "#A3E635", label: `${points?.referral_points ?? 100} pts / Referral` },
                { icon: Building2, bg: "#FDBA74", label: `${points?.admission_points ?? 5} pts / Admission` },
                { icon: MessageCircle, bg: "#93C5FD", label: `${points?.share_post_points ?? 10} pts / Post share` },
              ].map(({ icon: Icon, bg, label }) => (
                <div key={label} className="nb p-4 md:p-6" style={{ background: bg }}>
                  <span className="w-9 h-9 md:w-11 md:h-11 rounded-xl border-2 border-[#171B33] bg-white flex items-center justify-center"><Icon className="w-5 h-5" /></span>
                  <p className="font-display font-extrabold mt-3 md:mt-5 text-sm md:text-base">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <BannerStrip />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-10 md:py-16">
        <div className="flex items-end justify-between">
          <div>
            <p className="nb-label" style={{ letterSpacing: "0.2em" }}>Top ranked</p>
            <h2 className="font-display text-2xl sm:text-4xl font-extrabold tracking-tight mt-2">This week's leaders</h2>
          </div>
          <Link to="/rankings" data-testid="see-all-link" className="text-sm font-bold underline underline-offset-4 hover:text-lime-600 transition-colors duration-150">See all →</Link>
        </div>
        {all.length === 0 ? (
          <div data-testid="home-empty-state" className="nb-flat mt-8 p-12 text-center border-dashed">
            <GraduationCap className="w-10 h-10 mx-auto text-slate-400 mb-3" />
            <p className="font-bold">No institutions listed yet</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7">
            {all.slice(0, 6).map((inst, i) => <ListingCard key={inst.id} inst={inst} index={i} />)}
          </div>
        )}
      </section>

      {home?.show_cta !== false && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-4">
          <div className="nb p-8 md:p-14" style={{ background: "#171B33" }}>
            <p className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: "#A3E635" }}>For institutes</p>
            <h2 className="font-display text-2xl sm:text-4xl font-extrabold tracking-tight text-white mt-4 max-w-lg">{home?.cta_title}</h2>
            <p className="text-slate-300 mt-4 max-w-xl text-sm md:text-base">{home?.cta_text}</p>
            <Link to="/register" data-testid="cta-list-btn" className="nb-btn nb-btn-lime mt-8" style={{ boxShadow: "3px 3px 0 #EAB308" }}>List your institute <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </section>
      )}
    </div>
  );
}
